// Feature 009: Deal Operating System
// Modal for creating new opportunities
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useEffect, useRef } from 'react';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import SelectWithCreate from '../shared/SelectWithCreate';
import QuickCreatePartyModal from '../Parties/QuickCreatePartyModal';
import QuickCreateIpObjectModal from '../IpObjects/QuickCreateIpObjectModal';

const CATEGORIES = ['Sports', 'Music', 'Hospitality', 'Creator', 'Other'];
const CHANNELS = ['Ghost', 'DTC', 'Event'];

const NewOpportunityModal = ({ onClose, onSubmit, csrfToken }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Sports',
    primary_channel: '',
    counterparty_id: '',
    ip_object_id: '',
  });
  const [parties, setParties] = useState([]);
  const [ipObjects, setIpObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [showCreatePartyModal, setShowCreatePartyModal] = useState(false);
  const [showCreateIpObjectModal, setShowCreateIpObjectModal] = useState(false);
  const formRef = useRef(null);

  // Keyboard shortcuts for modal
  useKeyboardShortcuts({
    'Escape': () => onClose(),
    'ctrl+Enter': () => {
      if (formRef.current && formData.name && !loading) {
        formRef.current.requestSubmit();
      }
    },
    'cmd+Enter': () => {
      if (formRef.current && formData.name && !loading) {
        formRef.current.requestSubmit();
      }
    },
  }, true);

  useEffect(() => {
    // Load parties and IP objects for selection
    const fetchReferences = async () => {
      try {
        const [partiesRes, ipObjectsRes] = await Promise.all([
          fetch('/api/v1/parties?limit=100', {
            headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
            credentials: 'include',
          }),
          fetch('/api/v1/ip_objects?limit=100', {
            headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
            credentials: 'include',
          }),
        ]);

        if (partiesRes.ok) {
          const partiesData = await partiesRes.json();
          setParties(partiesData.parties || partiesData || []);
        }
        if (ipObjectsRes.ok) {
          const ipObjectsData = await ipObjectsRes.json();
          setIpObjects(ipObjectsData.ip_objects || ipObjectsData || []);
        }
      } catch (err) {
        console.error('Failed to load references:', err);
      } finally {
        setLoadingRefs(false);
      }
    };

    fetchReferences();
  }, [csrfToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit({
        ...formData,
        category: formData.category.toLowerCase(),
        primary_channel: formData.primary_channel?.toLowerCase() || null,
        counterparty_id: formData.counterparty_id || null,
        ip_object_id: formData.ip_object_id || null,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler for when a new party is created
  const handlePartyCreated = (newParty) => {
    setParties(prev => [...prev, newParty]);
    setFormData(prev => ({ ...prev, counterparty_id: newParty.id.toString() }));
    setShowCreatePartyModal(false);
  };

  // Handler for when a new IP object is created
  const handleIpObjectCreated = (newIpObject) => {
    setIpObjects(prev => [...prev, newIpObject]);
    setFormData(prev => ({ ...prev, ip_object_id: newIpObject.id.toString() }));
    setShowCreateIpObjectModal(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-80 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative inline-block align-bottom bg-[#0a0a0a] border border-[rgba(255,251,247,0.1)] text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Modal Header */}
            <div className="border-b border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 border border-[#a1c6a2] flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#a1c6a2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <span
                    className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block"
                    style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                  >
                    Create Record
                  </span>
                  <h3 className="text-lg font-semibold text-[#fffbf7] tracking-[0.05em] uppercase">
                    New Opportunity
                  </h3>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 space-y-5">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Opportunity Name <span className="text-[#c47474]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors placeholder:text-[rgba(255,251,247,0.3)]"
                  placeholder="e.g., Muhammad Ali Collection"
                />
              </div>

              {/* Category */}
              <div>
                <label
                  htmlFor="category"
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Category <span className="text-[#c47474]">*</span>
                </label>
                <select
                  name="category"
                  id="category"
                  required
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Primary Channel */}
              <div>
                <label
                  htmlFor="primary_channel"
                  className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-2"
                  style={{ fontFamily: "'GT Pressura Mono', monospace" }}
                >
                  Primary Channel
                </label>
                <select
                  name="primary_channel"
                  id="primary_channel"
                  value={formData.primary_channel}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[rgba(255,251,247,0.2)] bg-black text-[#fffbf7] text-sm focus:outline-none focus:border-[#a1c6a2] transition-colors"
                >
                  <option value="">-- Select Channel --</option>
                  {CHANNELS.map(ch => (
                    <option key={ch} value={ch}>{ch}</option>
                  ))}
                </select>
              </div>

              {/* Counterparty */}
              <SelectWithCreate
                label="Counterparty"
                name="counterparty_id"
                value={formData.counterparty_id}
                options={parties}
                onChange={(value) => setFormData(prev => ({ ...prev, counterparty_id: value }))}
                onCreateNew={() => setShowCreatePartyModal(true)}
                placeholder="-- Select Counterparty --"
                loading={loadingRefs}
                createLabel="New Party"
              />

              {/* IP Object */}
              <SelectWithCreate
                label="IP Object"
                name="ip_object_id"
                value={formData.ip_object_id}
                options={ipObjects}
                onChange={(value) => setFormData(prev => ({ ...prev, ip_object_id: value }))}
                onCreateNew={() => setShowCreateIpObjectModal(true)}
                placeholder="-- Select IP Object --"
                loading={loadingRefs}
                createLabel="New IP Object"
              />
            </div>

            {/* Modal Footer */}
            <div className="border-t border-[rgba(255,251,247,0.1)] px-6 py-4 bg-black flex flex-row-reverse gap-3">
              <button
                type="submit"
                disabled={loading || !formData.name}
                className="px-6 py-3 bg-[#fffbf7] text-black text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#a1c6a2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                {loading ? 'Creating...' : 'Create Opportunity'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-[rgba(255,251,247,0.2)] text-[rgba(255,251,247,0.6)] text-xs font-semibold tracking-[0.15em] uppercase hover:text-[#fffbf7] hover:border-[rgba(255,251,247,0.4)] transition-colors"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Quick Create Modals */}
      <QuickCreatePartyModal
        isOpen={showCreatePartyModal}
        onClose={() => setShowCreatePartyModal(false)}
        onCreated={handlePartyCreated}
      />

      <QuickCreateIpObjectModal
        isOpen={showCreateIpObjectModal}
        onClose={() => setShowCreateIpObjectModal(false)}
        onCreated={handleIpObjectCreated}
      />
    </div>
  );
};

export default NewOpportunityModal;
